import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from '../../features/auth/AuthContext';
import { apiHost } from "../../shared/api/helpers";



interface WebSocketMessage {
  id?: string;
  type?: string;
  content?: any;
  timestamp?: string;
  [key: string]: any;
}

interface WebSocketContextType {
  isConnected: boolean;
  lastMessage: WebSocketMessage | null;
  sendMessage: (message: any) => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);
const HEARTBEAT_INTERVAL_MS = 30000;
const HEARTBEAT_TIMEOUT_MS = 60000;

export const WebSocketProvider = ({ children }: { children: React.ReactNode }) => {
  const auth = useAuth();

  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const isConnectingRef = useRef<boolean>(false); // 연결 중 플래그 추가
  const reconnectTimerRef = useRef<number | null>(null);
  const heartbeatTimerRef = useRef<number | null>(null);
  const connectionTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const lastPongAtRef = useRef<number | null>(null);

  const getWebSocketUrl = (token: string) => {
    const url = new URL(`${apiHost}/ws/c/`);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.searchParams.set('token', token);
    return url.toString();
  };

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const clearHeartbeatTimer = () => {
    if (heartbeatTimerRef.current) {
      window.clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  };

  const clearConnectionTimeout = () => {
    if (connectionTimeoutRef.current) {
      window.clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }
  };

  const safeCloseSocket = () => {
    if (socketRef.current) {
      socketRef.current.onopen = null;
      socketRef.current.onmessage = null;
      socketRef.current.onclose = null;
      socketRef.current.onerror = null;
      socketRef.current.close();
      socketRef.current = null;
    }
  };

  useEffect(() => {
    if (!auth.isAuthenticated) {
      // 인증되지 않은 경우 연결 해제
      clearReconnectTimer();
      clearHeartbeatTimer();
      clearConnectionTimeout();
      safeCloseSocket();
      reconnectAttemptsRef.current = 0;
      isConnectingRef.current = false;
      setIsConnected(false);
      setLastMessage(null);
      return;
    }

    // 이미 연결되어 있거나 연결 중인 경우 중복 연결 방지
    if (socketRef.current?.readyState === WebSocket.OPEN ||
      socketRef.current?.readyState === WebSocket.CONNECTING ||
      isConnectingRef.current) {
      return;
    }

    const scheduleReconnect = () => {
      if (!auth.isAuthenticated || reconnectTimerRef.current) {
        return;
      }

      const nextAttempt = reconnectAttemptsRef.current + 1;
      reconnectAttemptsRef.current = nextAttempt;
      const delay = Math.min(30000, 1000 * Math.pow(2, Math.min(nextAttempt, 5)));
      console.warn(`WebSocket reconnect scheduled in ${delay}ms`);
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        void connectWebSocket();
      }, delay);
    };

    // WebSocket 연결 생성
    const connectWebSocket = async () => {
      if (!auth.isAuthenticated || isConnectingRef.current) {
        return;
      }

      if (socketRef.current?.readyState === WebSocket.OPEN ||
        socketRef.current?.readyState === WebSocket.CONNECTING) {
        return;
      }

      isConnectingRef.current = true;

      try {
        if (auth.isAccessTokenExpired()) {
          const refreshed = await auth.refreshToken();
          if (!refreshed) {
            scheduleReconnect();
            return;
          }
        }

        const token = auth.getAccessToken();
        if (!token || !auth.isAuthenticated) {
          return;
        }

        clearReconnectTimer();
        clearHeartbeatTimer();
        clearConnectionTimeout();
        safeCloseSocket();

        const socket = new WebSocket(getWebSocketUrl(token));
        socketRef.current = socket;

        socket.onopen = () => {
          clearConnectionTimeout();
          console.log('WebSocket connection opened');
          setIsConnected(true);
          isConnectingRef.current = false;
          reconnectAttemptsRef.current = 0;
          lastPongAtRef.current = Date.now();

          heartbeatTimerRef.current = window.setInterval(() => {
            if (socket.readyState !== WebSocket.OPEN) {
              return;
            }

            const lastPongAt = lastPongAtRef.current;
            if (lastPongAt === null || Date.now() - lastPongAt >= HEARTBEAT_TIMEOUT_MS) {
              console.warn('WebSocket heartbeat timeout; reconnecting');
              clearHeartbeatTimer();
              socket.close(4000, 'heartbeat timeout');
              return;
            }

            socket.send(JSON.stringify({ type: 'ping', ts: Date.now() }));
          }, HEARTBEAT_INTERVAL_MS);
        };

        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            const rawMessage = data.message;
            const message: WebSocketMessage = typeof rawMessage === 'string'
              ? JSON.parse(rawMessage)
              : rawMessage;

            if (message?.type === 'pong') {
              lastPongAtRef.current = Date.now();
            }

            if (!message || message.type === 'pong') {
              return;
            }

            setLastMessage(message);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        socket.onclose = (event) => {
          clearConnectionTimeout();
          clearHeartbeatTimer();
          isConnectingRef.current = false;
          setIsConnected(false);
          lastPongAtRef.current = null;
          if (socketRef.current === socket) {
            socketRef.current = null;
          }

          console.warn(`WebSocket connection closed: code=${event.code}, reason=${event.reason || 'none'}`);
          scheduleReconnect();
        };

        socket.onerror = (error) => {
          console.error('WebSocket error:', error);
          clearConnectionTimeout();
          isConnectingRef.current = false;
          setIsConnected(false);
          scheduleReconnect();

          if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
            socket.close();
          }
        };

        connectionTimeoutRef.current = window.setTimeout(() => {
          if (socket.readyState === WebSocket.CONNECTING) {
            console.warn('WebSocket connection timeout');
            isConnectingRef.current = false;
            scheduleReconnect();
            socket.close();
          }
        }, 10000);
      } finally {
        isConnectingRef.current = false;
      }
    };

    const handleVisibilityOrOnline = () => {
      const readyState = socketRef.current?.readyState;
      const isOpen = readyState === WebSocket.OPEN;
      const isConnecting = readyState === WebSocket.CONNECTING || isConnectingRef.current;
      const isHeartbeatStale = isOpen &&
        lastPongAtRef.current !== null &&
        Date.now() - lastPongAtRef.current >= HEARTBEAT_TIMEOUT_MS;

      if (!auth.isAuthenticated || isConnecting) return;
      if (isOpen && !isHeartbeatStale) return;

      if (isHeartbeatStale) {
        console.warn('Stale WebSocket detected after visibility/online change; reconnecting');
        clearHeartbeatTimer();
        safeCloseSocket();
        setIsConnected(false);
      }

      void connectWebSocket();
    };

    void connectWebSocket();
    document.addEventListener('visibilitychange', handleVisibilityOrOnline);
    window.addEventListener('online', handleVisibilityOrOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrOnline);
      window.removeEventListener('online', handleVisibilityOrOnline);
      clearReconnectTimer();
      clearHeartbeatTimer();
      clearConnectionTimeout();
      safeCloseSocket();
      isConnectingRef.current = false;
    };
  }, [auth.isAuthenticated]); // auth.getAccessToken 제거

  const sendMessage = (message: any) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected');
    }
  };

  return (
    <WebSocketContext.Provider
      value={{
        isConnected,
        lastMessage,
        sendMessage,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export type { WebSocketMessage, WebSocketContextType };
